import { captureAnalyticsEvent } from './posthog'

export type SpLinkChannel = 'email' | 'sms' | 'unknown'
export type SpOtpChannel = 'email' | 'sms'
export type SpCompletionCta = 'claim_reward' | 'go_home'
export type SpStoreTarget = 'google_playstore' | 'app_store'

const SURVEY_LINK_DEDUPE_PREFIX = 'ph_sp_link_'

function detectDeviceOs(): string {
  const ua = globalThis.navigator?.userAgent ?? ''
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios'
  if (/android/i.test(ua)) return 'android'
  return 'other'
}

/** Parse campaign `lc` query param (email|sms); unknown when absent (BFP-492 S4). */
export function parseSurveyLinkChannel(search: string): SpLinkChannel {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
  const lc = params.get('lc')?.trim().toLowerCase()
  if (lc === 'email' || lc === 'sms') return lc
  return 'unknown'
}

/** One capture per instrument per tab session (BFP-492 §11.2). */
export function shouldCaptureSurveyLinkClick(instrumentId: string): boolean {
  if (!instrumentId || globalThis.sessionStorage === undefined) return true
  const key = `${SURVEY_LINK_DEDUPE_PREFIX}${instrumentId}`
  try {
    if (globalThis.sessionStorage.getItem(key)) return false
    globalThis.sessionStorage.setItem(key, '1')
    return true
  } catch {
    return true
  }
}

export function captureSpSurveyLinkClicked(props: {
  link_channel: SpLinkChannel
  instrument_id: string
  sp_user_id?: string | null
}): void {
  captureAnalyticsEvent('sp_survey_link_clicked', {
    link_channel: props.link_channel,
    instrument_id: props.instrument_id,
    ...(props.sp_user_id ? { sp_user_id: props.sp_user_id } : {}),
  })
}

export function captureSpOtpChannelSelected(props: {
  otp_channel: SpOtpChannel
  instrument_id: string
  sp_user_id?: string | null
}): void {
  captureAnalyticsEvent('sp_otp_channel_selected', {
    otp_channel: props.otp_channel,
    instrument_id: props.instrument_id,
    ...(props.sp_user_id ? { sp_user_id: props.sp_user_id } : {}),
  })
}

export function captureSpSurveyCompletionCtaClicked(props: {
  cta_selected: SpCompletionCta
  instrument_id: string
  reward_amount_usd: number
}): void {
  captureAnalyticsEvent('sp_survey_completion_cta_clicked', {
    cta_selected: props.cta_selected,
    instrument_id: props.instrument_id,
    reward_amount_usd: props.reward_amount_usd,
  })
}

/** BFP-492-04 — no sp_user_count per operator decision. */
export function captureSpAccountActivationNotificationEngaged(spUserId: string): void {
  captureAnalyticsEvent('sp_account_activation_notification_engaged', {
    notification_type: 'account_activation',
    notification_channel: 'push_notification',
    sp_user_id: spUserId,
  })
}

export function captureSpPasswordResetCompleted(spUserId: string): void {
  captureAnalyticsEvent('sp_password_reset_completed', {
    reset_trigger: 'post_otp_login',
    sp_user_id: spUserId,
    reset_method: 'email_link',
  })
}

export function captureSpMobileStoreBannerClicked(props: {
  store_target: SpStoreTarget
  sp_user_id?: string | null
}): void {
  captureAnalyticsEvent('sp_mobile_store_banner_clicked', {
    store_target: props.store_target,
    device_os: detectDeviceOs(),
    ...(props.sp_user_id ? { sp_user_id: props.sp_user_id } : {}),
  })
}
